import type { LineAreaCapabilities } from '../../../chart-components/unified-line-area-chart/unified-line-area-chart.types';
import { toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';

/**
 * Pure helpers around line & area capabilities: folding the Format
 * accordion / tile Format popup into them, and the display-name remap the
 * compute API expects. Used by the step and by the shell's Format popup.
 */
export function applyLineAreaFormatToCapabilities(
capabilities: LineAreaCapabilities,
format: Record<string, unknown> | undefined,
resolvePaletteColors: (paletteKey?: string) => string[] | undefined,
): LineAreaCapabilities {
  const f = format || {};
  capabilities.showValues = f['showValues'] === true;
  capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : toNumberScale(f['numberFormat']);
  capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
  capabilities.showLegend = f['showLegend'] !== false;
  capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
  capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
  capabilities.paletteColors = resolvePaletteColors(capabilities.palette);
  capabilities.colors = undefined;
  capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
  capabilities.yAxisLabel = (f['yAxisLabel'] as string) || capabilities.yAxisLabel || undefined;
  capabilities.xAxisLabel2 = (f['xAxisLabel2'] as string) || capabilities.xAxisLabel2 || undefined;
  capabilities.yAxisLabel2 = (f['yAxisLabel2'] as string) || capabilities.yAxisLabel2 || undefined;
  capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
  capabilities.trimLabelPrefix = f['trimLabelPrefix'] === undefined ? undefined : f['trimLabelPrefix'] === true;
  const sxDisplay = String(f['secondaryXDisplay'] ?? '');
  capabilities.secondaryXAsTable = sxDisplay === 'table' ? true : sxDisplay === 'labels' ? false : undefined;
  capabilities.showGridLines = f['showGridLines'] !== false;
  capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
  capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
  capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
  capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
  capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
  capabilities.fontFamily = f['fontFamily'] ? String(f['fontFamily']) : undefined;
  capabilities.fontSize = Number(f['fontSize']) > 0 ? Number(f['fontSize']) : undefined;
  capabilities.axisFontWeight = f['axisFontWeight'] ? String(f['axisFontWeight']) : undefined;
  if (f['lineWidth'] !== undefined && f['lineWidth'] !== '' && f['lineWidth'] !== null) capabilities.lineWidth = Number(f['lineWidth']);
  if (f['symbolSize'] !== undefined && f['symbolSize'] !== '' && f['symbolSize'] !== null) capabilities.symbolSize = Number(f['symbolSize']);
  if (f['areaOpacity'] !== undefined && f['areaOpacity'] !== '' && f['areaOpacity'] !== null) capabilities.areaOpacity = Number(f['areaOpacity']);
  capabilities.smoothLines = f['smoothLines'] === true;
  capabilities.connectNulls = f['connectNulls'] === true;
  capabilities.showTrendLine = f['showTrendLine'] === true;
  capabilities.forecastPeriods = f['forecastPeriods'] !== undefined && f['forecastPeriods'] !== '' && f['forecastPeriods'] !== null ? Number(f['forecastPeriods']) : undefined;
  capabilities.movingAverageWindow = f['movingAverageWindow'] !== undefined && f['movingAverageWindow'] !== '' && f['movingAverageWindow'] !== null ? Number(f['movingAverageWindow']) : undefined;
  capabilities.analyticsColor = (f['analyticsColor'] as string) || undefined;
  capabilities.sorting = buildLineAreaSorting(f) as Array<{ field: string; direction?: string }> | undefined;
  capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
  capabilities.topN = f['topN'] !== undefined && f['topN'] !== '' && f['topN'] !== null ? Number(f['topN']) : undefined;
  capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
  capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
  capabilities.valueAxisLog = f['valueAxisLog'] === true;
  capabilities.animation = f['animation'] !== false;
  capabilities.enableZoom = f['enableZoom'] === true;
  capabilities.showZoomSlider = f['showZoomSlider'] !== false;
  capabilities.highlightOnClick = f['highlightOnClick'] === true;
  capabilities.crossFilter = f['crossFilter'] === true;
  return capabilities;
}

function buildLineAreaSorting(format?: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
  const f = format || {};
  const action = String(f['dataAction'] ?? '');
  const field = String(f['dataColumn'] ?? '').trim();
  if (action !== 'orderBy' || !field) return undefined;
  return [{ field, direction: 'ASC' }];
}

/** The compute API addresses columns by display name: remap category / measures / secondary-X value accordingly. */
export function remapLineAreaCapabilitiesToDisplayNames(capabilities: LineAreaCapabilities): LineAreaCapabilities {
  const displayCategory = capabilities.categoryLabel || capabilities.category;
  const remapped: LineAreaCapabilities = {
    ...capabilities,
    category: displayCategory,
    measures: (capabilities.measures || []).map((measure) => ({ ...measure, field: measure.label || measure.field })),
    ...(displayCategory !== capabilities.category
      ? { crossFilterKey: capabilities.crossFilterKey || capabilities.category }
      : {}),
  };
  if (remapped.secondaryXValueField) {
    const measure = (capabilities.measures || []).find((m) => m.field === capabilities.secondaryXValueField);
    if (measure) remapped.secondaryXValueField = measure.label || measure.field;
  }
  delete remapped.sorting;
  return remapped;
}
